/**LAGOSCarbonValueBridge — /state/lagos/carbon-value — UPGRADED with tiers */
import React, { useState } from "react";
import { useLagos } from "@/context/LAGOSContext";
import { PageHeader, SectionCard } from "@/components/ds";

const fmtN=(v,u="")=>{ if(!v&&v!==0)return"—"; const n=Number(v); if(n>=1e6)return`${(n/1e6).toFixed(1)}M ${u}`.trim(); if(n>=1000)return`${(n/1000).toFixed(0)}K ${u}`.trim(); return`${n.toLocaleString()} ${u}`.trim(); };
const fmtU=(v)=>{ if(!v)return"—"; const n=Number(v); if(n>=1e9)return`$${(n/1e9).toFixed(1)}B`; if(n>=1e6)return`$${(n/1e6).toFixed(1)}M`; if(n>=1000)return`$${(n/1000).toFixed(0)}K`; return`$${n}`; };

export default function LAGOSCarbonValueBridge() {
  const { portfolio, loading } = useLagos();
  const [filter, setFilter] = useState("all");
  const opps = (portfolio.top_opportunities||[]).filter(o =>
    filter==="all" || (filter==="art6"&&o.art6_eligible) || (filter==="vcm"&&o.vcm_eligible&&!o.art6_eligible)
  );
  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  // Operational value = circularity credit (material sales + tipping fees — bankable)
  const opVal = fmtU(portfolio.recycle_credit_tco2e * 15);  // ~$15/tonne material equivalent

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <PageHeader label="CARBON VALUE BRIDGE" title="" />
      <p className="text-xs text-slate-400 -mt-4">Operational value (bankable) · Carbon value tiers (indicative) · DSCR disclosure</p>

      {/* Three-value architecture */}
      <div className="grid grid-cols-3 gap-4">
        {/* Section A: Operational — bankable */}
        <div className="bg-white rounded-xl border-2 border-emerald-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[9px] font-black px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase tracking-wider">BANKABLE</span>
            <span className="text-xs font-bold text-slate-400">Section A — Operational Value</span>
          </div>
          <p className="text-2xl font-black text-emerald-700">{opVal}/yr</p>
          <p className="text-xs text-slate-500 mt-1">Circularity credit: material sales revenue from {fmtN(portfolio.recycle_credit_tco2e,"tCO₂e/yr")} recycled/diverted</p>
          <div className="mt-3 space-y-1 text-xs">
            <div className="flex justify-between border-b border-slate-50 py-1"><span className="text-slate-400">Tipping fee revenue</span><span className="font-bold">Included in entity P&L</span></div>
            <div className="flex justify-between border-b border-slate-50 py-1"><span className="text-slate-400">Material sales</span><span className="font-bold">{fmtU(portfolio.recycle_credit_tco2e * 10)}/yr est.</span></div>
            <div className="flex justify-between py-1"><span className="text-slate-400">CRG suitability</span><span className="font-bold text-emerald-700">High — bankable collateral</span></div>
          </div>
          <p className="text-[9px] text-emerald-700 font-medium mt-3 p-2 bg-emerald-50 rounded">Included in base DSCR · PFI-eligible</p>
        </div>

        {/* Section B: Carbon value — conditional */}
        <div className="bg-white rounded-xl border-2 border-blue-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[9px] font-black px-2 py-0.5 rounded bg-amber-100 text-amber-700 uppercase tracking-wider">CONDITIONAL</span>
            <span className="text-xs font-bold text-slate-400">Section B — Carbon Value</span>
          </div>
          <p className="text-2xl font-black text-[#1d4ed8]">{fmtU(portfolio.carbon_value_base_usd)}/yr</p>
          <p className="text-xs text-slate-500 mt-1">Base scenario ($20/t) · {fmtN(portfolio.reduction_potential_tco2e,"tCO₂e/yr")} reduction potential</p>
          <div className="mt-3 space-y-1 text-xs">
            {[["Low ($10/t)",fmtU(portfolio.carbon_value_low_usd)],["Base ($20/t)",fmtU(portfolio.carbon_value_base_usd)],["High ($40/t)",fmtU(portfolio.carbon_value_high_usd)]].map(([l,v])=>(
              <div key={l} className="flex justify-between border-b border-slate-50 py-1">
                <span className="text-slate-400">{l}</span><span className="font-bold text-[#1d4ed8]">{v}/yr</span>
              </div>
            ))}
            <div className="flex justify-between py-1"><span className="text-slate-400">VCM / Art.6.4 split</span><span className="font-bold">74.7% / 25.3%</span></div>
          </div>
          <p className="text-[9px] text-red-600 font-medium mt-3 p-2 bg-red-50 border border-red-100 rounded">NOT included in DSCR until VVB validated and registry-issued</p>
        </div>

        {/* Section C: Combined + PoA */}
        <div className="bg-white rounded-xl border-2 border-purple-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[9px] font-black px-2 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wider">PORTFOLIO</span>
            <span className="text-xs font-bold text-slate-400">Section C — Combined Upside</span>
          </div>
          <p className="text-2xl font-black text-purple-700">{fmtU((portfolio.carbon_value_base_usd||0) + (portfolio.recycle_credit_tco2e||0)*15)}/yr</p>
          <p className="text-xs text-slate-500 mt-1">Full portfolio: operational + carbon base combined</p>
          <div className="mt-3 space-y-1 text-xs">
            {[["VCM eligible entities",portfolio.vcm_eligible_count+" entities"],["Art.6.4 eligible (conditional)",portfolio.art6_eligible_count+" entities"],["CPA groups active","5 programmes"],["PoA eligible","4 CPAs → 1 state PoA"]].map(([l,v])=>(
              <div key={l} className="flex justify-between border-b border-slate-50 py-1">
                <span className="text-slate-400 flex-shrink-0">{l}</span><span className="font-bold text-right">{v}</span>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-purple-700 font-medium mt-3 p-2 bg-purple-50 rounded">Section A is bankable today. Section B is structuring upside, 18–36 month horizon.</p>
        </div>
      </div>

      {/* DSCR disclosure */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
        <strong>Mandatory DSCR Disclosure:</strong> Carbon credit values (Section B) are INDICATIVE and conditional on: (1) VVB/DOE validation, (2) registry issuance, and (3) for Art.6.4: NCCC corresponding adjustment authorisation. Carbon revenue MUST NOT be included in base DSCR calculations or PFI commitment letters until formally validated and issued. Operational circularity value (Section A) may be included in base case financial modelling subject to PFI appraisal.
      </div>

      {/* Opportunity table */}
      <SectionCard title="Top Carbon Opportunities">
        <div className="px-4 pt-3 flex gap-2">
          {[["all","All"],["art6","Art.6.4 (Conditional)"],["vcm","VCM"]].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)} className={`text-xs px-3 py-1 rounded-full border ${filter===v?"bg-[#1d4ed8] text-white border-[#1d4ed8]":"bg-white text-slate-500 border-slate-200"}`}>{l}</button>
          ))}
        </div>
        <div className="overflow-x-auto p-2">
          <table className="w-full text-xs mt-2">
            <thead><tr className="border-b border-slate-100">
              {["#","Entity","LGA","Sector","Reduction","Carbon (Base)","Pathway","Section A+B"].map(h=><th key={h} className="text-left py-2 px-3 text-slate-400 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {opps.map((o,i)=>(
                <tr key={o.entity_id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2 px-3 text-slate-400">{i+1}</td>
                  <td className="py-2 px-3 font-medium max-w-[140px] truncate">{o.name}</td>
                  <td className="py-2 px-3 text-slate-500">{o.lga}</td>
                  <td className="py-2 px-3 text-slate-500 capitalize">{(o.sector||"").replace(/_/g," ")}</td>
                  <td className="py-2 px-3 text-emerald-700 font-bold">{fmtN(o.reduction_potential_tco2e,"tCO₂e")}</td>
                  <td className="py-2 px-3">{fmtU(Number(o.reduction_potential_tco2e||0)*20)}/yr</td>
                  <td className="py-2 px-3">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${o.art6_eligible?"bg-amber-50 text-amber-700 border border-amber-200":"bg-blue-50 text-blue-700"}`}>
                      {o.art6_eligible?"Art.6.4 (conditional)":"VCM"}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-purple-700 font-bold">{fmtU(Number(o.reduction_potential_tco2e||0)*22)}/yr</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
